import { useBehaviorSubjectValue } from '@sas-dvr/internal-va-react-core/config/utils';
import { BehaviorSubject } from 'rxjs';
import { getSearchParam, getSearchParamFlag } from '../utils/searchParams';

export type GlobalFeatures = {
  manageColumns: boolean;
};
export type GlobalFeatureOverrides = Partial<GlobalFeatures>;

type GlobalFeatureMetaEntry<T> = {
  default: T;
  parameter: 'auto' | 'none' | ((value: string) => T | null);
};

const globalFeatureMeta: {
  [K in keyof GlobalFeatures]: GlobalFeatureMetaEntry<GlobalFeatures[K]>;
} = {
  manageColumns: {
    default: false,
    parameter: 'auto',
  },
};

const globalFeatureDefaults: GlobalFeatures = {} as GlobalFeatures;
const globalFeatureParameterOverrides: GlobalFeatureOverrides = {};

for (const [key, meta] of Object.entries(globalFeatureMeta)) {
  type AnyFeatureValue = GlobalFeatures[keyof GlobalFeatures];
  const feature = key as keyof GlobalFeatures;

  // Collect defaults from meta
  (globalFeatureDefaults[feature] as AnyFeatureValue) = meta.default;

  // Collect url parameter overrides
  let urlValue = null;
  const parameterName = `VASDK_${feature.toUpperCase()}`;
  if (meta.parameter === 'auto') {
    if (typeof meta.default === 'boolean') {
      urlValue = getSearchParamFlag(parameterName);
    } else if (typeof meta.default === 'string') {
      urlValue = getSearchParam(parameterName);
    } else {
      console.error(`Automatic url parameter not supported for ${feature}`);
    }
  } else if (typeof meta.parameter === 'function') {
    const value = getSearchParam(parameterName);
    if (value !== null) {
      urlValue = meta.parameter(value);
    }
  }
  if (urlValue !== null) {
    (globalFeatureParameterOverrides[feature] as AnyFeatureValue) = urlValue as AnyFeatureValue;
  }
}

let globalFeatureOverrides: GlobalFeatureOverrides = {};
function calculateGlobalFeatures() {
  return {
    ...globalFeatureDefaults,
    ...globalFeatureOverrides,
    ...globalFeatureParameterOverrides,
  };
}

export const globalFeatures$ = new BehaviorSubject<GlobalFeatures>(calculateGlobalFeatures());

/**
 * Set feature overrides using the given bundle. Omitted features return to their default value.
 */
export function setGlobalFeatures(features: GlobalFeatureOverrides) {
  // shallow copy to prevent untracked mutation.
  globalFeatureOverrides = { ...features };
  globalFeatures$.next(calculateGlobalFeatures());
}

/**
 * Set a single feature value.
 */
export function setGlobalFeature<T extends keyof GlobalFeatures>(
  feature: T,
  value: GlobalFeatures[T]
) {
  setGlobalFeatures({
    ...globalFeatureOverrides,
    [feature]: value,
  });
}
/**
 * Returns the current value of all features. URL parameters take precedence over dynamic settings.
 */
export function getGlobalFeatures(): GlobalFeatures {
  return globalFeatures$.value;
}
/**
 * Returns the current value of a specific feature
 */
export function getGlobalFeature<T extends keyof GlobalFeatures>(feature: T): GlobalFeatures[T] {
  return getGlobalFeatures()[feature];
}
/**
 * Hook that returns the current value of all features.
 */
export function useGlobalFeatures(): GlobalFeatures {
  return useBehaviorSubjectValue(globalFeatures$);
}
/**
 * Hook that returns the current value of a specific feature.
 */
export function useGlobalFeature<T extends keyof GlobalFeatures>(feature: T): GlobalFeatures[T] {
  return useGlobalFeatures()[feature];
}
